import { create } from "zustand";
import { BuildPlatform } from "../core_stores/useProjectBuildsStore";

export enum DashboardMode {
  WORKSPACE = "workspace",
  PREVIEW = "preview",
}

export enum WorkspaceSection {
  // OVERVIEW = "overview",
  ICON = "icon",
  CODE = "code",
  DEBUG = "debug",
  SETTINGS = "settings",
  PUBLISH = "publish",
  EXPORT = "export",
}

interface DashboardControlsActions {
  setDashboardMode: (dashboardMode: DashboardMode | null) => void;
  setWorkspaceSection: (workspaceSection: WorkspaceSection | null) => void;
  goBackInCompactView: () => void;
  setPublishPlatform: (publishPlatform: BuildPlatform) => void;
  resetWorkspaceHistory: () => void;
  reset: () => void;
}

interface DashboardControlsState {
  dashboardMode: DashboardMode | null;
  workspaceSection: WorkspaceSection | null;
  workspaceSectionHistory: WorkspaceSection[];
  currentHistoryIndex: number;
  selectedPublishPlatform: BuildPlatform;

  actions: DashboardControlsActions;
}

const useDashboardControlsStore = create<DashboardControlsState>()(
  (set, get) => {
    return {
      isDisabled: true,
      dashboardMode: null,
      workspaceSection: null,
      workspaceSectionHistory: [],
      currentHistoryIndex: -1,
      selectedPublishPlatform: BuildPlatform.WEB,

      actions: {
        setDashboardMode: (dashboardMode: DashboardMode | null) => {
          set({ dashboardMode });
        },

        setWorkspaceSection: (workspaceSection: WorkspaceSection | null) => {
          const currentState = get();

          // Only track history if we're in workspace mode and the section is actually changing
          if (
            currentState.dashboardMode === DashboardMode.WORKSPACE &&
            currentState.workspaceSection !== workspaceSection
          ) {
            const history = [...currentState.workspaceSectionHistory];
            // Remove any future history if we're not at the end
            if (currentState.currentHistoryIndex < history.length - 1) {
              history.splice(currentState.currentHistoryIndex + 1);
            }
            if (currentState.workspaceSection) {
              history.push(currentState.workspaceSection);
            }

            set({
              workspaceSection,
              workspaceSectionHistory: history,
              currentHistoryIndex: history.length - 1,
            });
          } else {
            set({ workspaceSection });
          }
        },

        goBackInCompactView: () => {
          const currentState = get();
          // Only allow going back if we're in workspace mode and have history
          if (
            currentState.dashboardMode === DashboardMode.WORKSPACE &&
            currentState.currentHistoryIndex >= 0
          ) {
            const previousSection =
              currentState.workspaceSectionHistory[
                currentState.currentHistoryIndex
              ];
            set({
              workspaceSection: previousSection,
              currentHistoryIndex: currentState.currentHistoryIndex - 1,
            });
          } else {
            set({
              dashboardMode: null,
              workspaceSection: null,
            });
          }
        },

        setPublishPlatform: (publishPlatform: BuildPlatform) => {
          set({ selectedPublishPlatform: publishPlatform });
        },

        resetWorkspaceHistory: () => {
          set({
            workspaceSectionHistory: [],
            currentHistoryIndex: -1,
          });
        },

        reset: () => {
          set({
            dashboardMode: null,
            workspaceSection: WorkspaceSection.SETTINGS,
            workspaceSectionHistory: [],
            currentHistoryIndex: -1,
          });
        },
      },
    };
  }
);

export const useDashboardMode = () =>
  useDashboardControlsStore((state) => state.dashboardMode);

export const useWorkspaceSection = () =>
  useDashboardControlsStore((state) => state.workspaceSection);

export const useSelectedPublishPlatform = () =>
  useDashboardControlsStore((state) => state.selectedPublishPlatform);

export const useDashboardControlsActions = () =>
  useDashboardControlsStore((state) => state.actions);
